/**
 * ScadBlock — unified MDX component for OpenSCAD embeds.
 *
 * Replaces both ScadPreview and ScadPlayground with a single flexible
 * component. The post author chooses a compact display variant; every
 * instance (except "showcase") exposes three floating toggles and can
 * expand in-place to a full Monaco editor + 3D viewer + parameter panel.
 *
 * Variants:
 *   "viewer"   — 3D viewer only (was ScadPreview)
 *   "snippet"  — read-only code excerpt + 3D viewer
 *   "editor"   — editable Monaco + 3D viewer (was ScadPlayground)
 *   "showcase" — chrome-free auto-rotating hero embed, no expand
 *
 * Expand behaviour:
 *   All non-showcase variants show three overlay toggles (Presentation
 *   Mode, Fit to View, Expand). Clicking Expand grows the component
 *   in-place (CSS height transition) to show the full Monaco editor,
 *   viewer, and parameter panel. If the compact variant was "snippet",
 *   the expanded editor highlights the snippet lines with a persistent
 *   Monaco decoration and scrolls to them.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Editor, {
  type BeforeMount,
  type OnMount,
  type Monaco,
} from '@monaco-editor/react';
import { useTheme } from '@/contexts/ThemeContext';
import { ViewerSection } from '@/components/viewer/ViewerSection';
import { ParameterSection } from '@/components/parameter/ParameterSection';
import { BlobProvider } from '@/contexts/BlobContext';
import { ColorProvider } from '@/contexts/ColorContext';
import { SceneSettingsProvider } from '@/contexts/SceneSettingsContext';
import { parseParameters, updateParameter } from '@/utils/parameterUtils';
import { getFileType, isBinaryFileType } from '@/components/files/FileManager';
import type { ProjectFile } from '@/components/files/FileManager';
import type { Parameter, InteractiveConfig } from '@shared/types';
import { waitForSlot, releaseSlot, registerInstance } from './stagger';
import {
  definePlaygroundThemes,
  registerOpenSCADLanguage,
} from './monacoSetup';
import { ScadBlockToolbar } from './ScadBlockToolbar';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A file to make available to OpenSCAD's import() function. */
export interface ScadBlockFile {
  /** Filename including extension, e.g. "logo.svg" or "part.stl" */
  filename: string;
  /**
   * Raw file content as a string (text files) or ArrayBuffer (binary files).
   * For SVG / DXF / SCAD, import the file with Vite's `?raw` suffix.
   */
  content: string | ArrayBuffer;
}

export type ScadBlockVariant = 'viewer' | 'snippet' | 'editor' | 'showcase';

export interface ScadBlockProps {
  // ── Code ────────────────────────────────────────────────────────────────
  /** Inline OpenSCAD code. Pass string[] for tabbed progression mode. */
  code?: string | string[];
  /**
   * Raw SCAD source imported via `?raw` (single-file alternative to `code`).
   *   import myCode from './example.scad?raw'
   *   <ScadBlock src={myCode} />
   */
  src?: string;
  /** Auxiliary files for OpenSCAD's import() function. */
  files?: ScadBlockFile[];

  // ── Variant ─────────────────────────────────────────────────────────────
  /**
   * Compact display variant. Default: "viewer".
   * Passing code as string[] forces "editor" regardless of this prop.
   */
  variant?: ScadBlockVariant;

  // ── Snippet (variant="snippet") ─────────────────────────────────────────
  /**
   * Text to display in the compact read-only code block.
   * Mutually exclusive with snippetLines.
   * If neither is provided, the full code is shown.
   */
  snippet?: string;
  /**
   * 1-indexed [startLine, endLine] range within `code` to display as the
   * compact snippet AND to highlight in the expanded editor.
   * Mutually exclusive with snippet.
   */
  snippetLines?: [number, number];

  // ── Layout ──────────────────────────────────────────────────────────────
  /** Height of the component in compact state (px). Default: 400 */
  height?: number;
  /** Height of the component in expanded state (px). Default: 680 */
  expandedHeight?: number;
  /**
   * Fraction of height allocated to the Monaco editor in editor/expanded
   * state (0–1). Default: 0.4
   */
  editorRatio?: number;
  /**
   * Show the interactive parameter panel when the code contains parameter
   * annotations. Default: true
   */
  showParameters?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derive a tab label from a code string.
 * Rule: first non-empty `// …` comment → strip prefix; else `Step N`.
 */
function extractLabel(code: string, index: number): string {
  for (const raw of code.split('\n')) {
    const line = raw.trim();
    if (line.startsWith('//')) {
      const label = line.slice(2).trim();
      if (label) return label;
    }
    if (line && !line.startsWith('//') && !line.startsWith('/*')) break;
  }
  return `Step ${index + 1}`;
}

/**
 * Given a full code string and a snippet string, find the 1-indexed
 * [startLine, endLine] of the snippet within the full code.
 * Returns null if the snippet cannot be located.
 */
function findSnippetLines(
  fullCode: string,
  snippet: string,
): [number, number] | null {
  const fullLines = fullCode.split('\n');
  const snippetLines = snippet.split('\n');
  if (snippetLines.length === 0) return null;

  // Find where the first line of the snippet appears in the full code
  const firstSnippetLine = snippetLines[0].trim();
  for (let i = 0; i < fullLines.length; i++) {
    if (fullLines[i].trim() === firstSnippetLine) {
      // Verify the rest of the snippet matches
      let match = true;
      for (let j = 1; j < snippetLines.length; j++) {
        if (
          i + j >= fullLines.length ||
          fullLines[i + j].trim() !== snippetLines[j].trim()
        ) {
          match = false;
          break;
        }
      }
      if (match) {
        return [i + 1, i + snippetLines.length]; // 1-indexed
      }
    }
  }
  return null;
}

/**
 * Derive the snippet text to display in compact "snippet" variant.
 * Priority: snippetLines prop → snippet prop → full code.
 */
function resolveSnippetText(
  fullCode: string,
  snippet?: string,
  snippetLines?: [number, number],
): string {
  if (snippetLines) {
    const lines = fullCode.split('\n');
    const [start, end] = snippetLines;
    return lines.slice(start - 1, end).join('\n');
  }
  if (snippet !== undefined) return snippet;
  return fullCode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScadBlock({
  code,
  src,
  height = 400,
  expandedHeight = 680,
  editorRatio = 0.4,
  showParameters = true,
  files,
  variant: variantProp = 'viewer',
  snippet,
  snippetLines,
}: ScadBlockProps) {
  // ── Normalise variant ────────────────────────────────────────────────────
  // Tabbed progression always needs editor mode
  const isTabbed = Array.isArray(code) && code.length > 1;
  const variant: ScadBlockVariant = isTabbed ? 'editor' : variantProp;

  if (process.env.NODE_ENV !== 'production') {
    if (isTabbed && variantProp !== 'editor' && variantProp !== 'viewer') {
      console.warn(
        '[ScadBlock] code[] (tabbed progression) forces variant="editor". ' +
          `Received variant="${variantProp}" — ignored.`,
      );
    }
    if (snippet !== undefined && snippetLines !== undefined) {
      console.warn(
        '[ScadBlock] snippet and snippetLines are mutually exclusive. ' +
          'snippetLines takes precedence.',
      );
    }
  }

  // ── Steps (normalise code to array) ────────────────────────────────────
  const steps: string[] = Array.isArray(code) ? code : [code ?? src ?? ''];
  const labels = steps.map(extractLabel);

  // ── Project files ───────────────────────────────────────────────────────
  const projectFiles = useMemo<ProjectFile[]>(() => {
    if (!files || files.length === 0) return [];
    return files.map((f) => {
      const fileType = getFileType(f.filename);
      const isBinary = isBinaryFileType(fileType);
      const size =
        typeof f.content === 'string' ? f.content.length : f.content.byteLength;
      return {
        id: f.filename,
        filename: f.filename,
        fileType,
        content: f.content,
        size,
        isBinary,
      };
    });
  }, [files]);

  // ── Tab / progression state ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [tabCodes, setTabCodes] = useState<string[]>(steps);

  // Reset if the source prop changes (e.g. hot-reload in dev)
  useEffect(() => {
    setTabCodes(steps);
    setActiveTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.join('\n---\n')]);

  const activeCode = tabCodes[activeTab] ?? '';

  // ── Viewer / parameter state ────────────────────────────────────────────
  const [scadCode, setScadCode] = useState(activeCode);
  const [parameters, setParameters] = useState<Parameter[]>(() =>
    parseParameters(activeCode),
  );

  // Sync when active tab changes
  useEffect(() => {
    setScadCode(activeCode);
    setParameters(parseParameters(activeCode));
  }, [activeCode]);

  // ── Expand / collapse ───────────────────────────────────────────────────
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setIsExpanded((v) => !v), []);

  // ── Presentation mode (shared across expand/collapse) ──────────────────
  const [presentationMode, setPresentationMode] = useState(false);

  // ── Fit-to-bounds callback (set by ViewerSection) ───────────────────────
  const [fitToBoundsCallback, setFitToBoundsCallback] = useState<
    (() => void) | null
  >(null);

  // ── Open in playground ──────────────────────────────────────────────────
  const handleOpenInPlayground = useCallback(() => {
    localStorage.setItem('scadblock_open_in_playground', scadCode);
    window.open('/playground/new', '_blank', 'noopener');
  }, [scadCode]);

  // ── Viewport / stagger ──────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(registerInstance, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let cancelled = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          waitForSlot().then(() => {
            if (!cancelled) setIsVisible(true);
            else releaseSlot();
          });
        }
      },
      { rootMargin: '50px' },
    );
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  // ── Monaco editor ref / guards ──────────────────────────────────────────
  const editorInstanceRef = useRef<Parameters<OnMount>[0] | null>(null);
  const monacoInstanceRef = useRef<Monaco | null>(null);
  const isUiUpdateRef = useRef(false);
  // Decoration IDs for snippet highlight — cleared on first edit
  const snippetDecorationsRef = useRef<string[]>([]);

  // ── Theme ───────────────────────────────────────────────────────────────
  const { resolvedTheme } = useTheme();
  const themeName =
    resolvedTheme === 'dark' ? 'playground-dark' : 'playground-light';

  // ── Snippet highlight lines ─────────────────────────────────────────────
  // Computed once from props; used when expanding from "snippet" variant.
  const highlightRange = useMemo<[number, number] | null>(() => {
    if (variant !== 'snippet') return null;
    const fullCode = steps[0] ?? '';
    if (snippetLines) return snippetLines;
    if (snippet !== undefined) return findSnippetLines(fullCode, snippet);
    return null;
  }, [variant, snippet, snippetLines, steps]);

  // Apply snippet highlight decorations when editor mounts or isExpanded changes
  const applySnippetHighlight = useCallback(
    (editor: Parameters<OnMount>[0], monaco: Monaco) => {
      if (!highlightRange) return;
      const [startLine, endLine] = highlightRange;
      const decorations = editor.deltaDecorations(
        snippetDecorationsRef.current,
        [
          {
            range: new monaco.Range(startLine, 1, endLine, 9999),
            options: {
              className: 'scad-snippet-highlight',
              isWholeLine: true,
            },
          },
        ],
      );
      snippetDecorationsRef.current = decorations;
      editor.revealLineInCenter(startLine);

      // Clear on first user edit
      const disposable = editor.onDidChangeModelContent(() => {
        editor.deltaDecorations(snippetDecorationsRef.current, []);
        snippetDecorationsRef.current = [];
        disposable.dispose();
      });
    },
    [highlightRange],
  );

  // ── Monaco setup callbacks ──────────────────────────────────────────────
  const handleBeforeMount: BeforeMount = useCallback((monaco) => {
    definePlaygroundThemes(monaco);
    // Register the language early so the read-only snippet editor also gets
    // syntax highlighting (it shares the same Monaco instance).
    registerOpenSCADLanguage(monaco);
  }, []);

  // onMount for the read-only snippet editor: just stores the monaco instance
  // so we can apply highlights later. Language is already registered above.
  const handleSnippetEditorDidMount: OnMount = useCallback(
    (_editor, monaco) => {
      // Keep monaco ref available for potential future use; language is
      // registered via beforeMount so no extra work needed here.
      monacoInstanceRef.current = monaco;
    },
    [],
  );

  const handleEditorDidMount: OnMount = useCallback(
    (editor, monaco) => {
      editorInstanceRef.current = editor;
      monacoInstanceRef.current = monaco;
      // Language is registered in handleBeforeMount; no need to repeat here.

      // When expanding from snippet variant, isExpanded is already true when
      // this fires. Defer by one rAF to let Monaco finish its layout pass
      // before applying decorations.
      if (highlightRange) {
        requestAnimationFrame(() => {
          if (editorInstanceRef.current && monacoInstanceRef.current) {
            applySnippetHighlight(
              editorInstanceRef.current,
              monacoInstanceRef.current,
            );
          }
        });
      }
    },
    [applySnippetHighlight, highlightRange],
  );

  // ── Tab switch ──────────────────────────────────────────────────────────
  const switchTab = useCallback(
    (index: number) => {
      if (index === activeTab) return;
      const current = editorInstanceRef.current?.getValue();
      if (current !== undefined) {
        setTabCodes((prev) => {
          const next = [...prev];
          next[activeTab] = current;
          return next;
        });
      }
      setActiveTab(index);
    },
    [activeTab],
  );

  // ── Code change handler ─────────────────────────────────────────────────
  const handleCodeChange = useCallback(
    (value: string | undefined) => {
      if (value === undefined) return;
      setTabCodes((prev) => {
        const next = [...prev];
        next[activeTab] = value;
        return next;
      });
      setScadCode(value);
      if (!isUiUpdateRef.current) {
        setParameters(parseParameters(value));
      }
    },
    [activeTab],
  );

  // ── Parameter change handler ────────────────────────────────────────────
  const handleParametersChange = useCallback(
    (newParams: Parameter[]) => {
      let newCode = scadCode;
      newParams.forEach((param) => {
        newCode = updateParameter(newCode, param);
      });

      if (editorInstanceRef.current) {
        isUiUpdateRef.current = true;
        const model = editorInstanceRef.current.getModel();
        if (model) {
          editorInstanceRef.current.executeEdits('param-update', [
            { range: model.getFullModelRange(), text: newCode },
          ]);
        }
        isUiUpdateRef.current = false;
      }

      setTabCodes((prev) => {
        const next = [...prev];
        next[activeTab] = newCode;
        return next;
      });
      setScadCode(newCode);
      setParameters(newParams);
    },
    [scadCode, activeTab],
  );

  // ── Interactive region click handler ───────────────────────────────────
  const handleInteraction = useCallback(
    (config: InteractiveConfig) => {
      const param = parameters.find((p) => p.name === config.variable);
      if (!param) return;

      let newValue: Parameter['value'];
      if (config.options !== undefined && config.options.length > 0) {
        // Cycle mode: advance to the next option in the list, wrapping around.
        const current = param.value;
        const opts = config.options as Array<string | number>;
        const currentIdx = opts.findIndex((o) => String(o) === String(current));
        const nextIdx = currentIdx === -1 ? 0 : (currentIdx + 1) % opts.length;
        newValue = opts[nextIdx];
      } else if (config.value !== undefined) {
        newValue = config.value;
      } else {
        const current = param.value;
        newValue = current === true || current === 'true' ? false : true;
      }

      const updatedParam: Parameter = { ...param, value: newValue };
      handleParametersChange(
        parameters.map((p) => (p.name === config.variable ? updatedParam : p)),
      );
    },
    [parameters, handleParametersChange],
  );

  // ── Viewer fit-to-bounds ready callback ────────────────────────────────
  const handleFitToBoundsReady = useCallback((fitFn: () => void) => {
    setFitToBoundsCallback(() => fitFn);
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────
  const hasParameters = showParameters && parameters.length > 0;
  const currentHeight = isExpanded ? expandedHeight : height;

  // Snippet text for read-only Monaco in compact "snippet" variant
  const snippetText = useMemo(() => {
    const fullCode = steps[0] ?? '';
    return resolveSnippetText(fullCode, snippet, snippetLines);
  }, [steps, snippet, snippetLines]);

  const snippetPx = Math.round(height * 0.35);

  // ── Empty guard ─────────────────────────────────────────────────────────
  if (steps.every((s) => !s.trim())) {
    return (
      <div
        className="flex items-center justify-center rounded-md border border-dashed border-border bg-muted/30 text-sm text-muted-foreground"
        style={{ height }}
      >
        No SCAD code provided. Pass either a <code>code</code> or{' '}
        <code>src</code> prop.
      </div>
    );
  }

  // ── Showcase variant — no chrome, no expand ─────────────────────────────
  if (variant === 'showcase') {
    return (
      <div
        ref={containerRef}
        className="not-prose my-8 overflow-hidden rounded-lg border border-border bg-muted/30"
        style={{ height }}
      >
        {isVisible ? (
          <BlobProvider>
            <ColorProvider>
              <SceneSettingsProvider initialPerformanceMode={true}>
                <ViewerSection
                  scadCode={scadCode}
                  projectFiles={projectFiles}
                  showcaseMode
                  onFirstResult={releaseSlot}
                  parameters={parameters}
                />
              </SceneSettingsProvider>
            </ColorProvider>
          </BlobProvider>
        ) : (
          <LoadingSpinner />
        )}
      </div>
    );
  }

  // ── Editor dimensions when expanded ─────────────────────────────────────
  // In expanded state: header (40px) + editor (editorRatio) + viewer (rest)
  const expandedEditorPx = Math.round(expandedHeight * editorRatio);
  const expandedViewerPx = expandedHeight - 40 - expandedEditorPx; // 40 = header bar

  // ── Compact editor dimensions ────────────────────────────────────────────
  const compactEditorPx = Math.round(height * editorRatio);

  // Viewer height in compact state
  const compactViewerPx =
    variant === 'snippet'
      ? height - snippetPx
      : variant === 'editor'
        ? height - compactEditorPx
        : height;

  // ── Toolbar shared props ────────────────────────────────────────────────
  const toolbarProps = {
    presentationMode,
    onPresentationModeToggle: setPresentationMode,
    onFitToBounds: fitToBoundsCallback,
    isExpanded,
    onExpandToggle: toggleExpanded,
    onOpenInPlayground: handleOpenInPlayground,
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <>
      {/* Snippet highlight CSS — injected once, global */}
      <style>{snippetHighlightStyle}</style>

      <div
        ref={containerRef}
        className="not-prose my-8 overflow-hidden rounded-lg border border-border bg-muted/30 transition-[height] duration-300 ease-in-out"
        style={{ height: currentHeight }}
      >
        {!isVisible ? (
          <LoadingSpinner />
        ) : (
          <BlobProvider>
            <ColorProvider>
              <SceneSettingsProvider initialPerformanceMode={true}>
                <div className="flex h-full flex-col">
                  {/* ── Expanded header bar ────────────────────────────── */}
                  {isExpanded && (
                    <ScadBlockToolbar
                      mode="header"
                      label="ScadBlock"
                      {...toolbarProps}
                    />
                  )}

                  {/* ── Tab bar (progression mode, only in editor/expanded) */}
                  {isTabbed && (variant === 'editor' || isExpanded) && (
                    <div
                      className="flex flex-none items-stretch border-b border-border bg-background"
                      style={{ height: 36 }}
                    >
                      {labels.map((label, i) => (
                        <button
                          key={label}
                          onClick={() => switchTab(i)}
                          className={[
                            'relative px-4 text-xs font-medium transition-colors focus:outline-none',
                            i === activeTab
                              ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary'
                              : 'text-muted-foreground hover:text-foreground',
                          ].join(' ')}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* ── Compact snippet (read-only Monaco) ────────────── */}
                  {variant === 'snippet' && !isExpanded && (
                    <div
                      className="flex-none overflow-hidden border-b border-border"
                      style={{ height: snippetPx }}
                    >
                      <Editor
                        height="100%"
                        defaultLanguage="openscad"
                        language="openscad"
                        value={snippetText}
                        beforeMount={handleBeforeMount}
                        onMount={handleSnippetEditorDidMount}
                        theme={themeName}
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          fontFamily:
                            'JetBrains Mono, Menlo, Monaco, Consolas, monospace',
                          lineNumbers: 'off',
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          tabSize: 2,
                          wordWrap: 'on',
                          padding: { top: 10, bottom: 10 },
                          folding: false,
                          glyphMargin: false,
                          lineDecorationsWidth: 8,
                          renderLineHighlight: 'none',
                          scrollbar: {
                            verticalScrollbarSize: 6,
                            horizontalScrollbarSize: 6,
                          },
                          cursorStyle: 'line',
                          renderValidationDecorations: 'off',
                          contextmenu: false,
                        }}
                      />
                    </div>
                  )}

                  {/* ── Editable Monaco editor ─────────────────────────── */}
                  {(variant === 'editor' || isExpanded) && (
                    <div
                      className="flex-none overflow-hidden border-b border-border"
                      style={{
                        height: isExpanded ? expandedEditorPx : compactEditorPx,
                      }}
                    >
                      <Editor
                        height="100%"
                        defaultLanguage="openscad"
                        language="openscad"
                        path={`playground-step-${activeTab}.scad`}
                        keepCurrentModel={false}
                        value={activeCode}
                        onChange={handleCodeChange}
                        beforeMount={handleBeforeMount}
                        onMount={handleEditorDidMount}
                        theme={themeName}
                        options={{
                          minimap: { enabled: false },
                          fontSize: 13,
                          fontFamily:
                            'JetBrains Mono, Menlo, Monaco, Consolas, monospace',
                          lineNumbers: 'off',
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          tabSize: 2,
                          wordWrap: 'on',
                          padding: { top: 10, bottom: 10 },
                          folding: false,
                          glyphMargin: false,
                          lineDecorationsWidth: 8,
                          renderLineHighlight: 'none',
                          quickSuggestions: false,
                          suggestOnTriggerCharacters: false,
                          parameterHints: { enabled: false },
                          scrollbar: {
                            verticalScrollbarSize: 6,
                            horizontalScrollbarSize: 6,
                          },
                        }}
                      />
                    </div>
                  )}

                  {/* ── Viewer + parameter sidebar ─────────────────────── */}
                  <div
                    className="relative flex min-h-0 flex-1"
                    style={{
                      height:
                        variant === 'editor' && !isExpanded
                          ? compactViewerPx
                          : isExpanded
                            ? expandedViewerPx
                            : undefined,
                    }}
                  >
                    {/* Floating toolbar overlay (compact state) */}
                    {!isExpanded && (
                      <ScadBlockToolbar mode="floating" {...toolbarProps} />
                    )}

                    <div
                      className={
                        hasParameters
                          ? 'flex-1 overflow-hidden'
                          : 'h-full w-full'
                      }
                    >
                      <ViewerSection
                        scadCode={scadCode}
                        projectFiles={projectFiles}
                        productMode
                        showcaseMode={false}
                        onFirstResult={releaseSlot}
                        onFitToBoundsReady={handleFitToBoundsReady}
                        presentationMode={presentationMode}
                        parameters={parameters}
                        onInteraction={handleInteraction}
                      />
                    </div>

                    {hasParameters && (
                      <div className="w-64 flex-none overflow-hidden border-l border-border">
                        <ParameterSection
                          parameters={parameters}
                          onParametersChange={handleParametersChange}
                          scadCode={scadCode}
                          productMode
                        />
                      </div>
                    )}
                  </div>
                </div>
              </SceneSettingsProvider>
            </ColorProvider>
          </BlobProvider>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Loading spinner
// ---------------------------------------------------------------------------

function LoadingSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-muted-foreground" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Snippet highlight CSS (injected once via <style>)
// ---------------------------------------------------------------------------

const snippetHighlightStyle = `
.scad-snippet-highlight {
  background: color-mix(in srgb, var(--primary, #6366f1) 12%, transparent) !important;
}
`;
